/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

const BOOKSTORE_BACKGROUND_FILTER_WORKERS = 1;
const BOOKSTORE_BACKGROUND_FILTER_DELAY = 120;
const BOOKSTORE_COLLECTION_SEARCH_DEBOUNCE = 320;

function runBookstoreBackgroundTask(task, delay = BOOKSTORE_BACKGROUND_FILTER_DELAY) {
  return new Promise((resolve) => {
    const run = () => {
      Promise.resolve()
        .then(task)
        .then(resolve)
        .catch(resolve);
    };

    window.setTimeout(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(run, { timeout: 1500 });
      } else {
        run();
      }
    }, delay);
  });
}

/**
 * Old publisher links from search pages can still contain `filter.p.vendor`.
 * Collection/category pages must keep `filter.p.vendor` as an in-page filter,
 * so this safeguard only normalizes search-page links.
 */
function normalizePublisherVendorUrl(rawUrl) {
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    const vendor = parsed.searchParams.get('filter.p.vendor');

    if (!vendor) return null;

    if (parsed.pathname === '/search') {
      return `/collections/vendors?q=${encodeURIComponent(vendor)}${parsed.hash || ''}`;
    }
  } catch (error) {
    return null;
  }

  return null;
}

function rewritePublisherVendorLinks() {
  document.querySelectorAll('a[href*="filter.p.vendor"]').forEach((link) => {
    const normalizedUrl = normalizePublisherVendorUrl(link.href);

    if (normalizedUrl) {
      link.href = normalizedUrl;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rewritePublisherVendorLinks, { once: true });
} else {
  rewritePublisherVendorLinks();
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target ? target.closest('a[href*="filter.p.vendor"]') : null;

    if (!link) return;

    const normalizedUrl = normalizePublisherVendorUrl(link.href);

    if (!normalizedUrl) return;

    event.preventDefault();
    window.location.href = normalizedUrl;
  },
  true
);

document.addEventListener('shopify:section:load', rewritePublisherVendorLinks);

/**
 * Publisher chips inside collection heroes use the same in-page search box.
 * Clicking a chip simply places the publisher name in the collection search and
 * runs the already-working catalogue filter on the same page.
 */
function initCollectionPublisherChips() {
  const chips = Array.from(document.querySelectorAll('.bookstore-category-publishers a'));
  const input = document.querySelector('[data-bookstore-collection-search-input], input[name="collection_search"]');
  const url = new URL(window.location.href);
  const initialVendor = url.searchParams.get('filter.p.vendor') || '';
  const initialSearch = url.searchParams.get('collection_search') || '';

  if (!chips.length) return;

  const getChipSearchValue = (link) => {
    try {
      const parsed = new URL(link.href, window.location.origin);
      return parsed.searchParams.get('collection_search') || parsed.searchParams.get('filter.p.vendor') || link.textContent.trim();
    } catch (error) {
      return link.textContent.trim();
    }
  };

  const setActiveChip = (value) => {
    const normalizedValue = normalizeCollectionSearchText(value);

    chips.forEach((chip) => {
      const normalizedChip = normalizeCollectionSearchText(getChipSearchValue(chip));
      const isActive = normalizedValue && normalizedChip === normalizedValue;

      chip.style.display = normalizedValue && !isActive ? 'none' : '';

      if (isActive) {
        chip.setAttribute('aria-current', 'page');
      } else {
        chip.removeAttribute('aria-current');
      }
    });
  };

  chips.forEach((link) => {
    if (link.dataset.publisherChipFilterReady === 'true') return;

    link.dataset.publisherChipFilterReady = 'true';
    link.addEventListener('click', (event) => {
      event.preventDefault();

      const searchValue = getChipSearchValue(link);

      if (input) {
        input.value = searchValue;
        input.focus();
      }

      applyCollectionPageSearch(searchValue, true, true);
      setActiveChip(searchValue);
    });
  });

  if (initialVendor && input) {
    input.value = initialSearch;
    applyCollectionVendorFilterFromURL(true);
    setActiveChip(initialVendor);
  } else {
    setActiveChip(initialSearch);
  }
}

function getCollectionVendorFilter() {
  const url = new URL(window.location.href);
  return url.searchParams.get('filter.p.vendor') || '';
}

function uniqueBookstoreFilterValues(values) {
  const seen = new Set();
  const uniqueValues = [];

  values.forEach((rawValue) => {
    const value = String(rawValue || '').trim();
    const key = normalizeCollectionSearchText(value);
    if (!value || !key || seen.has(key)) return;

    seen.add(key);
    uniqueValues.push(value);
  });

  return uniqueValues;
}

function getCollectionVendorFilters(url = new URL(window.location.href)) {
  const values = url.searchParams.getAll('filter.p.vendor');

  if (url.pathname === '/collections/vendors') {
    values.push(url.searchParams.get('q') || '');
  }

  return uniqueBookstoreFilterValues(values);
}

function isBookstoreCollectionPath(pathname) {
  return String(pathname || '').split('/').filter(Boolean)[0] === 'collections';
}

function buildCleanBookstorePublisherFilterUrl(selectedVendors, sourceUrl = new URL(window.location.href), forceAllCollection = false) {
  const vendors = uniqueBookstoreFilterValues(selectedVendors);
  const shouldUseAllCollection = forceAllCollection || sourceUrl.pathname === '/collections/vendors';
  const next = new URL(shouldUseAllCollection ? '/collections/all' : sourceUrl.pathname, window.location.origin);

  sourceUrl.searchParams.forEach((value, name) => {
    const cleanValue = String(value || '').trim();
    if (!cleanValue || cleanValue === '*') return;
    if (name === 'page' || name === 'section_id') return;
    if (name === 'filter.p.vendor') return;
    if (name === 'q' || name === 'type' || name === 'view' || name === 'options[prefix]') return;

    if (
      name === 'sort_by' ||
      name === 'collection_search' ||
      name === 'author' ||
      name === 'bookstore_category' ||
      name.startsWith('filter.')
    ) {
      next.searchParams.append(name, cleanValue);
    }
  });

  vendors.forEach((vendor) => {
    next.searchParams.append('filter.p.vendor', vendor);
  });

  return next;
}

function getNextBookstorePublisherFilterUrl(input) {
  const current = new URL(window.location.href);
  if (!isBookstoreCollectionPath(current.pathname)) return null;

  const currentVendors = getCollectionVendorFilters(current);
  const formVendors = Array.from(input.form?.querySelectorAll('input[name="filter.p.vendor"]:checked') || [])
    .map((checkbox) => checkbox.value);
  const changedVendor = String(input.value || '').trim();
  const selectedVendors = uniqueBookstoreFilterValues([...currentVendors, ...formVendors]);

  if (changedVendor) {
    const changedKey = normalizeCollectionSearchText(changedVendor);
    const existingIndex = selectedVendors.findIndex((vendor) => normalizeCollectionSearchText(vendor) === changedKey);

    if (input.checked && existingIndex === -1) {
      selectedVendors.push(changedVendor);
    } else if (!input.checked && existingIndex !== -1) {
      selectedVendors.splice(existingIndex, 1);
    }
  }

  return buildCleanBookstorePublisherFilterUrl(selectedVendors, current, current.pathname === '/collections/vendors' || selectedVendors.length > 1);
}

function syncBookstorePublisherFilterInputs(root = document) {
  const scope = root instanceof Element ? root : document;
  const selectedKeys = new Set(getCollectionVendorFilters().map((vendor) => normalizeCollectionSearchText(vendor)));

  scope.querySelectorAll?.('input[name="filter.p.vendor"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const isSelected = selectedKeys.has(normalizeCollectionSearchText(input.value));
    input.checked = isSelected;
    input.closest('.bookstore-collection-facet__link')?.classList.toggle('is-active', isSelected);
  });
}

function cleanupBookstorePublisherFilterUrl() {
  const current = new URL(window.location.href);
  if (!isBookstoreCollectionPath(current.pathname)) return;

  const vendors = getCollectionVendorFilters(current);
  const shouldNormalize =
    current.searchParams.has('page') ||
    Boolean(current.hash) ||
    current.searchParams.getAll('filter.p.vendor').length !== vendors.length ||
    Array.from(current.searchParams.entries()).some(([name, value]) => {
      const cleanValue = String(value || '').trim();
      return !cleanValue || cleanValue === '*' || name === 'type' || name === 'view' || name === 'options[prefix]';
    });

  if (!shouldNormalize || (current.pathname === '/collections/vendors' && vendors.length <= 1 && !current.searchParams.has('filter.p.vendor'))) return;

  const next = buildCleanBookstorePublisherFilterUrl(vendors, current, current.pathname === '/collections/vendors' && vendors.length > 1);
  window.history.replaceState({}, '', `${next.pathname}${next.search}`);
}

function initBookstorePublisherFilterControls(root = document) {
  if (root === document) cleanupBookstorePublisherFilterUrl();
  syncBookstorePublisherFilterInputs(root);
  renderBookstoreSelectedFilterSummary(root);
}

document.addEventListener(
  'change',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const input = target?.matches('input[name="filter.p.vendor"]') ? target : null;

    if (!(input instanceof HTMLInputElement)) return;

    const nextUrl = getNextBookstorePublisherFilterUrl(input);
    if (!nextUrl) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    window.location.href = `${nextUrl.pathname}${nextUrl.search}`;
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBookstorePublisherFilterControls(document), { once: true });
} else {
  initBookstorePublisherFilterControls(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initBookstorePublisherFilterControls(event.target instanceof Element ? event.target : document);
});

function getCollectionPublisherChipValue(link) {
  try {
    const parsed = new URL(link.href, window.location.origin);
    return parsed.searchParams.get('collection_search') || parsed.searchParams.get('filter.p.vendor') || link.textContent.trim();
  } catch (error) {
    return link.textContent.trim();
  }
}

function syncCollectionPublisherChips(query) {
  const chips = Array.from(document.querySelectorAll('.bookstore-category-publishers a'));
  const normalizedQuery = normalizeCollectionSearchText(query);

  if (!chips.length) return;

  const hasExactPublisher = normalizedQuery && chips.some((chip) => {
    return normalizeCollectionSearchText(getCollectionPublisherChipValue(chip)) === normalizedQuery;
  });

  chips.forEach((chip) => {
    const isActive = hasExactPublisher && normalizeCollectionSearchText(getCollectionPublisherChipValue(chip)) === normalizedQuery;

    chip.style.display = hasExactPublisher && !isActive ? 'none' : '';

    if (isActive) {
      chip.setAttribute('aria-current', 'page');
    } else {
      chip.removeAttribute('aria-current');
    }
  });
}

function productItemMatchesVendor(item, vendor) {
  const normalizedVendor = normalizeCollectionSearchText(vendor);
  if (!normalizedVendor) return true;

  const exactVendor = normalizeCollectionSearchText(item.getAttribute('data-bookstore-product-vendor') || '');
  if (exactVendor) return exactVendor === normalizedVendor;

  const rawText = item.getAttribute('data-collection-search-text') || item.textContent || '';
  const searchableText = normalizeCollectionSearchText(rawText);

  return normalizedVendor.split(' ').filter(Boolean).every((token) => searchableText.includes(token));
}

function productItemMatchesSelectedVendors(item, url = new URL(window.location.href)) {
  const vendors = getCollectionVendorFilters(url);
  if (!vendors.length) return true;

  return vendors.some((vendor) => productItemMatchesVendor(item, vendor));
}

function getCollectionAuthorFilters(url = new URL(window.location.href)) {
  const values = url.searchParams.getAll('filter.p.m.custom.author');

  if (!values.length) {
    values.push(url.searchParams.get('author') || '');
  }

  return uniqueBookstoreFilterValues(values).filter((value) => value !== '*');
}

function renderBookstoreSelectedFilterSummary(root = document) {
  const scope = root instanceof Element ? root : document;
  const containers = Array.from(scope.querySelectorAll?.('[data-bookstore-selected-filter-summary]') || []);
  if (!containers.length) return;

  const url = new URL(window.location.href);
  const selectedFilters = [
    ...getCollectionVendorFilters(url).map((value) => ({ label: 'Publisher', value })),
    ...getCollectionAuthorFilters(url).map((value) => ({ label: 'Author', value })),
  ];

  containers.forEach((container) => {
    container.hidden = selectedFilters.length === 0;
    container.innerHTML = selectedFilters.map((filter) => {
      return `<span class="bookstore-selected-filter-summary__pill"><span>${escapeCollectionSearchHtml(filter.label)}</span>${escapeCollectionSearchHtml(filter.value)}</span>`;
    }).join('');
  });
}

function productItemMatchesSearch(item, query) {
  const normalizedQuery = normalizeCollectionSearchText(query);
  if (!normalizedQuery) return true;

  const rawText = item.getAttribute('data-collection-search-text') || item.textContent || '';
  const searchableText = normalizeCollectionSearchText(rawText);
  const words = searchableText.split(' ').filter(Boolean);

  return normalizedQuery.split(' ').filter(Boolean).every((token) => {
    return words.some((word) => word.startsWith(token));
  });
}

function getBookstoreActivePriceValue(paramName, url = new URL(window.location.href)) {
  const rawValue = url.searchParams.get(paramName);
  if (!rawValue) return null;

  const parsedValue = Number(String(rawValue).replace(/,/g, '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function getBookstoreProductPriceRange(item) {
  const minPrice = Number(item.getAttribute('data-bookstore-product-price-min'));
  const maxPrice = Number(item.getAttribute('data-bookstore-product-price-max'));

  if (!Number.isFinite(minPrice) && !Number.isFinite(maxPrice)) return null;

  const safeMin = Number.isFinite(minPrice) ? minPrice : maxPrice;
  const safeMax = Number.isFinite(maxPrice) ? maxPrice : minPrice;

  return {
    min: safeMin / 100,
    max: safeMax / 100,
  };
}

function productItemMatchesActivePrice(item, url = new URL(window.location.href)) {
  const minFilter = getBookstoreActivePriceValue('filter.v.price.gte', url);
  const maxFilter = getBookstoreActivePriceValue('filter.v.price.lte', url);

  if (minFilter === null && maxFilter === null) return true;

  const range = getBookstoreProductPriceRange(item);
  if (!range) return true;

  if (minFilter !== null && range.max < minFilter) return false;
  if (maxFilter !== null && range.min > maxFilter) return false;

  return true;
}

function hasBookstoreActivePriceFilter(url = new URL(window.location.href)) {
  return getBookstoreActivePriceValue('filter.v.price.gte', url) !== null || getBookstoreActivePriceValue('filter.v.price.lte', url) !== null;
}

function getBookstoreCollectionHandleFromPath(pathname) {
  const pathParts = String(pathname || '').split('/').filter(Boolean);
  if (pathParts[0] !== 'collections') return '';

  const handle = pathParts[1] || '';
  if (!handle || handle === 'all' || handle === 'vendors' || handle === 'frontpage') return '';

  try {
    return decodeURIComponent(handle);
  } catch (error) {
    return handle;
  }
}

function getBookstoreExtraCategoryFilters(url = new URL(window.location.href)) {
  return url.searchParams
    .getAll('bookstore_category')
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

function getBookstoreSelectedCategoryHandles(url = new URL(window.location.href)) {
  const handles = [];
  const currentHandle = getBookstoreCollectionHandleFromPath(url.pathname);

  if (currentHandle) handles.push(currentHandle);
  getBookstoreExtraCategoryFilters(url).forEach((handle) => handles.push(handle));

  return handles.filter((handle, index) => {
    if (!handle || handle === 'all') return false;
    return handles.indexOf(handle) === index;
  });
}

function normalizeAllCollectionCategoryUrl() {
  const current = new URL(window.location.href);
  if (current.pathname !== '/collections/all') return;

  const selectedCategories = getBookstoreExtraCategoryFilters(current);
  if (!selectedCategories.length) return;

  const targetHandle = selectedCategories.find((handle) => handle && handle !== 'all');
  if (!targetHandle) return;

  const next = new URL(current.href);
  const remainingCategories = selectedCategories.filter((handle) => handle && handle !== targetHandle);

  next.pathname = `/collections/${encodeURIComponent(targetHandle)}`;
  next.searchParams.delete('bookstore_category');
  remainingCategories.forEach((handle) => {
    appendUniqueBookstoreSearchParam(next.searchParams, 'bookstore_category', handle);
  });
  next.searchParams.delete('page');
  next.searchParams.delete('section_id');

  window.location.replace(`${next.pathname}${next.search}${next.hash}`);
}

normalizeAllCollectionCategoryUrl();

function productItemMatchesCollectionHandle(item, handle) {
  const normalizedHandle = normalizeCollectionSearchText(handle);
  if (!normalizedHandle) return true;

  const collectionHandles = String(item.getAttribute('data-bookstore-product-collections') || '')
    .split('|')
    .map((value) => normalizeCollectionSearchText(value))
    .filter(Boolean);

  if (collectionHandles.length) {
    return collectionHandles.includes(normalizedHandle);
  }

  const rawText = item.getAttribute('data-collection-search-text') || item.textContent || '';
  const searchableText = normalizeCollectionSearchText(rawText);

  return searchableText.split(' ').filter(Boolean).includes(normalizedHandle);
}

function productItemMatchesExtraCategories(item, url = new URL(window.location.href)) {
  const selectedCategories = getBookstoreExtraCategoryFilters(url);
  if (!selectedCategories.length) return true;

  return selectedCategories.some((handle) => productItemMatchesCollectionHandle(item, handle));
}

function productItemMatchesCurrentCollectionPath(item, url = new URL(window.location.href)) {
  const currentHandle = getBookstoreCollectionHandleFromPath(url.pathname);
  if (!currentHandle) return true;

  return productItemMatchesCollectionHandle(item, currentHandle);
}

function productItemMatchesActiveCategories(item, url = new URL(window.location.href)) {
  const selectedCategories = getBookstoreSelectedCategoryHandles(url);
  if (!selectedCategories.length) return true;

  return selectedCategories.some((handle) => productItemMatchesCollectionHandle(item, handle));
}

function getCollectionTotalCount() {
  const countNode = document.querySelector('main[data-template^="collection"] [data-bookstore-visible-count]');
  const rawCount = countNode?.getAttribute('data-bookstore-total-count') || countNode?.dataset.bookstoreOriginalCount || '';
  const parsed = Number(String(rawCount).replace(/[^\d]/g, ''));

  if (Number.isFinite(parsed) && parsed > 0) return parsed;

  const grid = getCollectionSearchGrid();
  const fallback = Number(grid?.dataset.bookstoreOriginalItemCount || 0);

  return Number.isFinite(fallback) && fallback > 0 ? fallback : getCollectionSearchItems().length;
}

function setCollectionVisibleCount(count) {
  const safeCount = Number.isFinite(Number(count)) ? Number(count) : getCollectionTotalCount();

  document.querySelectorAll('main[data-template^="collection"] [data-bookstore-visible-count]').forEach((node) => {
    node.textContent = String(safeCount);
  });

  document.querySelectorAll('main[data-template^="collection"] .products-count-wrapper span[role="status"]').forEach((node) => {
    node.textContent = `${safeCount} ${safeCount === 1 ? 'item' : 'items'}`;
  });
}

function syncCollectionVisibleCountFromDom() {
  const visibleCount = getCollectionSearchItems().filter((item) => {
    return !item.hidden && item.style.display !== 'none';
  }).length;

  setCollectionVisibleCount(visibleCount);
  return visibleCount;
}

function setBookstoreFilterLoadingState(isLoading) {
  document.documentElement.classList.toggle('bookstore-filtering-pending', isLoading);

  document.querySelectorAll('main[data-template^="collection"] [data-bookstore-visible-count]').forEach((node) => {
    if (isLoading) {
      if (!node.dataset.bookstoreLoadingPreviousText) {
        node.dataset.bookstoreLoadingPreviousText = node.textContent.trim();
      }
      node.textContent = '...';
    } else if (node.dataset.bookstoreLoadingPreviousText) {
      delete node.dataset.bookstoreLoadingPreviousText;
    }
  });
}

function hasBookstoreClientSideCollectionFilters(url = new URL(window.location.href)) {
  const selectedVendors = getCollectionVendorFilters(url);
  const hasNativeSingleVendorPage = shouldUseNativeVendorPageOnly(selectedVendors, url);

  return (
    getBookstoreExtraCategoryFilters(url).length > 0 ||
    Boolean((url.searchParams.get('collection_search') || '').trim()) ||
    hasBookstoreActivePriceFilter(url) ||
    (selectedVendors.length > 0 && !hasNativeSingleVendorPage)
  );
}

function waitForBookstoreRenderFrame() {
  return new Promise((resolve) => {
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(() => window.setTimeout(resolve, 0));
    } else {
      window.setTimeout(resolve, 0);
    }
  });
}

function filterExistingCollectionItemsByVendor(vendor) {
  const items = getCollectionSearchItems();
  const grid = getCollectionSearchGrid();
  const wrapper = document.querySelector('main[data-template^="collection"] collection-component');
  const selectedVendors = getCollectionVendorFilters();
  const selectedCategories = getBookstoreExtraCategoryFilters();
  const hasActivePrice = hasBookstoreActivePriceFilter();
  let visibleCount = 0;

  items.forEach((item) => {
    const isMatch = productItemMatchesSelectedVendors(item) && productItemMatchesActiveCategories(item) && productItemMatchesActivePrice(item);

    item.hidden = !isMatch;
    item.style.display = isMatch ? '' : 'none';

    if (isMatch) visibleCount += 1;
  });

  if (wrapper) {
    wrapper.classList.toggle('bookstore-collection-vendor-filter-active', selectedVendors.length > 0);
    wrapper.classList.toggle('bookstore-collection-category-filter-active', selectedCategories.length > 0);
    wrapper.classList.toggle('bookstore-collection-price-filter-active', hasActivePrice);
  }

  const message = ensureCollectionSearchMessage(grid);

  if (message) {
    message.textContent = selectedVendors.length ? `No books found for selected publishers in this collection.` : 'No books found for selected filters in this collection.';
    message.hidden = (selectedVendors.length === 0 && selectedCategories.length === 0 && !hasActivePrice) || visibleCount > 0;
  }

  setCollectionVisibleCount(selectedVendors.length || selectedCategories.length || hasActivePrice ? visibleCount : items.length);

  return visibleCount;
}

async function loadAllCollectionPagesForVendorFilter() {
  const selectedVendors = getCollectionVendorFilters();
  if (!selectedVendors.length) return;

  const grid = getCollectionSearchGrid();
  const resultsList = document.querySelector('main[data-template^="collection"] results-list[section-id]');
  const sectionId = resultsList?.getAttribute('section-id') || '';
  const currentCategory = getBookstoreCollectionHandleFromPath(window.location.pathname);
  const categoryKey = getBookstoreExtraCategoryFilters().join('|');
  const loadedKey = `${selectedVendors.join('|')}:${currentCategory}:${categoryKey}`;

  if (!grid || grid.dataset.vendorFilterLoaded === loadedKey || grid.dataset.vendorFilterLoading === loadedKey) return;
  grid.dataset.vendorFilterLoading = loadedKey;

  const runId = (window.__bookstoreVendorFilterRunId || 0) + 1;
  window.__bookstoreVendorFilterRunId = runId;
  const isCurrentRun = () => window.__bookstoreVendorFilterRunId === runId && grid.dataset.vendorFilterLoading === loadedKey;

  const existingIds = new Set(
    Array.from(grid.querySelectorAll('.product-grid__item[data-product-id]')).map((item) => item.getAttribute('data-product-id'))
  );
  let loadedVisibleCount = filterExistingCollectionItemsByVendor(getCollectionVendorFilter());
  let hasRenderedFirstVendorBatch = false;

  const appendPageItems = async (pageItems) => {
    const fragment = document.createDocumentFragment();
    let appendedCount = 0;

    for (const item of pageItems) {
      if (!isCurrentRun()) return;

      const productId = item.getAttribute('data-product-id');
      if (!productId || existingIds.has(productId)) continue;
      existingIds.add(productId);

      item.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
        image.setAttribute('fetchpriority', 'low');
      });
      item.hidden = false;
      item.style.display = '';
      fragment.appendChild(item);
      appendedCount += 1;
    }

    if (appendedCount > 0) {
      grid.dataset.bookstoreBatchAppending = 'true';
      try {
        grid.appendChild(fragment);
        loadedVisibleCount += appendedCount;
        setCollectionVisibleCount(loadedVisibleCount);
        if (!hasRenderedFirstVendorBatch) {
          hasRenderedFirstVendorBatch = true;
          setBookstoreFilterLoadingState(false);
        }
        await waitForBookstoreRenderFrame();
      } finally {
        delete grid.dataset.bookstoreBatchAppending;
      }
    }
  };

  async function fetchVendorPage(vendorName, page = 1) {
    const url = new URL('/collections/vendors', window.location.origin);
    url.searchParams.set('q', vendorName);
    if (page > 1) url.searchParams.set('page', String(page));
    if (sectionId) url.searchParams.set('section_id', sectionId);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 12000);

    try {
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      });
      if (!response.ok) return { vendorName, page, pages: 1, items: [] };

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextItems = Array.from(doc.querySelectorAll('.product-grid__item[data-product-id]'));
      const matchingItems = nextItems.filter((item) => productItemMatchesSelectedVendors(item) && productItemMatchesActiveCategories(item) && productItemMatchesActivePrice(item));
      const pages = Number(doc.querySelector('.product-grid[data-last-page]')?.getAttribute('data-last-page') || 1);

      return { vendorName, page, pages, items: matchingItems };
    } catch (error) {
      // Keep current visible matches if one extra page request fails.
      return { vendorName, page, pages: 1, items: [] };
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  const firstPages = await Promise.all(selectedVendors.map((vendorName) => fetchVendorPage(vendorName, 1)));
  if (!isCurrentRun()) return;

  const remainingPages = [];
  firstPages.forEach((result, index) => {
    const vendorName = selectedVendors[index];
    for (let page = 2; page <= result.pages; page += 1) {
      remainingPages.push({ vendorName, page });
    }
  });

  for (const result of firstPages) {
    await appendPageItems(result.items);
  }

  if (!hasRenderedFirstVendorBatch) {
    hasRenderedFirstVendorBatch = true;
  }
  setBookstoreFilterLoadingState(false);

  if (!remainingPages.length) {
    if (isCurrentRun()) {
      grid.dataset.vendorFilterLoaded = loadedKey;
      delete grid.dataset.vendorFilterLoading;
    }
    return;
  }

  window.setTimeout(async () => {
    let nextPageIndex = 0;
    const workerCount = Math.min(BOOKSTORE_BACKGROUND_FILTER_WORKERS, Math.max(1, remainingPages.length));

    try {
      await Promise.all(
        Array.from({ length: workerCount }, async () => {
          while (nextPageIndex < remainingPages.length && isCurrentRun()) {
            const task = remainingPages[nextPageIndex];
            nextPageIndex += 1;
            await runBookstoreBackgroundTask(async () => {
              const result = await fetchVendorPage(task.vendorName, task.page);
              await appendPageItems(result.items);
            });
          }
        })
      );
    } finally {
      if (isCurrentRun()) {
        grid.dataset.vendorFilterLoaded = loadedKey;
        delete grid.dataset.vendorFilterLoading;
        filterExistingCollectionItemsByVendor(getCollectionVendorFilter());
        syncCollectionVisibleCountFromDom();
        setBookstoreFilterLoadingState(false);
      }
    }
  }, 50);
}

function shouldUseNativeVendorPageOnly(selectedVendors, url = new URL(window.location.href)) {
  return (
    url.pathname === '/collections/vendors' &&
    selectedVendors.length === 1 &&
    getBookstoreExtraCategoryFilters(url).length === 0 &&
    !hasBookstoreActivePriceFilter(url) &&
    !url.searchParams.get('collection_search')
  );
}

async function applyCollectionVendorFilterFromURL(loadAllPages = false) {
  const vendor = getCollectionVendorFilter();
  const selectedVendors = getCollectionVendorFilters();
  const shouldUseClientSideFilters = hasBookstoreClientSideCollectionFilters();
  const shouldLoadAllPages = selectedVendors.length && loadAllPages && shouldUseClientSideFilters && !shouldUseNativeVendorPageOnly(selectedVendors);

  if (shouldLoadAllPages) {
    setBookstoreFilterLoadingState(true);
  }

  document.querySelectorAll('.bookstore-category-publishers a[href*="filter.p.vendor"]').forEach((link) => {
    const linkVendor = new URL(link.href, window.location.origin).searchParams.get('filter.p.vendor') || '';
    if (vendor && linkVendor === vendor) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });

  if (!shouldUseClientSideFilters) {
    setBookstoreFilterLoadingState(false);
    renderBookstoreActiveQueryPills();
    return getCollectionSearchItems().length;
  }

  let visibleCount = filterExistingCollectionItemsByVendor(vendor);

  try {
    if (shouldLoadAllPages) {
      await loadAllCollectionPagesForVendorFilter();
      visibleCount = filterExistingCollectionItemsByVendor(vendor);
    }
  } finally {
    setBookstoreFilterLoadingState(false);
  }

  return visibleCount;
}

async function loadAllCollectionPagesForExtraCategoryFilters() {
  const currentHandle = getBookstoreCollectionHandleFromPath(window.location.pathname);
  const hasActivePrice = hasBookstoreActivePriceFilter();
  let selectedCategories = getBookstoreSelectedCategoryHandles();

  if (!selectedCategories.length && hasActivePrice) {
    selectedCategories = [currentHandle || 'all'];
  }

  if (!selectedCategories.length) return;

  const resultsList = document.querySelector('main[data-template^="collection"] results-list[section-id]');
  const grid = getCollectionSearchGrid();
  const vendorKey = getCollectionVendorFilters().join('|');
  const searchKey = new URL(window.location.href).searchParams.get('collection_search') || '';
  const priceKey = `${getBookstoreActivePriceValue('filter.v.price.gte') ?? ''}:${getBookstoreActivePriceValue('filter.v.price.lte') ?? ''}`;
  const loadedKey = `${selectedCategories.join('|')}:${vendorKey}:${searchKey}:${priceKey}`;

  if (!resultsList || !grid || grid.dataset.categoryFilterLoaded === loadedKey || grid.dataset.categoryFilterLoading === loadedKey) return;
  grid.dataset.categoryFilterLoading = loadedKey;

  const runId = (window.__bookstoreCategoryFilterRunId || 0) + 1;
  window.__bookstoreCategoryFilterRunId = runId;
  const isCurrentRun = () => window.__bookstoreCategoryFilterRunId === runId && grid.dataset.categoryFilterLoading === loadedKey;

  const sectionId = resultsList.getAttribute('section-id');

  if (!sectionId) {
    grid.dataset.categoryFilterLoaded = loadedKey;
    delete grid.dataset.categoryFilterLoading;
    return;
  }

  const existingIds = new Set(
    Array.from(grid.querySelectorAll('.product-grid__item[data-product-id]')).map((item) => item.getAttribute('data-product-id'))
  );
  let loadedVisibleCount = filterExistingCollectionItemsByVendor(getCollectionVendorFilter());

  const appendCategoryItems = async (pageItems) => {
    const fragment = document.createDocumentFragment();
    let appendedCount = 0;

    for (const item of pageItems) {
      if (!isCurrentRun()) return;

      const productId = item.getAttribute('data-product-id');
      if (!productId || existingIds.has(productId)) continue;
      existingIds.add(productId);

      item.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
        image.setAttribute('fetchpriority', 'low');
      });
      item.hidden = false;
      item.style.display = '';
      fragment.appendChild(item);
      appendedCount += 1;
    }

    if (appendedCount > 0) {
      grid.dataset.bookstoreBatchAppending = 'true';
      try {
        grid.appendChild(fragment);
        loadedVisibleCount += appendedCount;
        setCollectionVisibleCount(loadedVisibleCount);
        await waitForBookstoreRenderFrame();
      } finally {
        delete grid.dataset.bookstoreBatchAppending;
      }
    }
  };

  function buildCategoryPageUrl(handle, page) {
    const url = new URL(`/collections/${encodeURIComponent(handle)}`, window.location.origin);
    const current = new URL(window.location.href);

    current.searchParams.forEach((value, name) => {
      if (!value || name === 'page' || name === 'section_id' || name === 'bookstore_category') return;
      url.searchParams.append(name, value);
    });

    if (page > 1) url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);
    return url;
  }

  async function loadCategoryPage(handle, page) {
    const url = buildCategoryPageUrl(handle, page);

    try {
      const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) return { handle, page, pages: 1, items: [] };

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextItems = Array.from(doc.querySelectorAll('.product-grid__item[data-product-id]'));
      const matchingItems = nextItems.filter((item) => productItemMatchesSelectedVendors(item) && productItemMatchesActiveCategories(item) && productItemMatchesActivePrice(item));
      const pages = Number(doc.querySelector('.product-grid[data-last-page]')?.getAttribute('data-last-page') || 1);

      return { handle, page, pages, items: matchingItems };
    } catch (error) {
      // Keep current visible matches if one extra page request fails.
      return { handle, page, pages: 1, items: [] };
    }
  }

  const firstPageTasks = selectedCategories
    .filter((handle) => handle !== currentHandle)
    .map((handle) => loadCategoryPage(handle, 1));

  const firstPages = await Promise.all(firstPageTasks);
  if (!isCurrentRun()) return;

  for (const result of firstPages) {
    await appendCategoryItems(result.items);
  }

  const remainingPages = [];
  if (currentHandle && selectedCategories.includes(currentHandle)) {
    const currentLastPage = Number(grid.dataset.lastPage || 1);
    for (let page = 2; page <= currentLastPage; page += 1) {
      remainingPages.push({ handle: currentHandle, page });
    }
  } else if (selectedCategories.includes('all')) {
    const currentLastPage = Number(grid.dataset.lastPage || 1);
    for (let page = 2; page <= currentLastPage; page += 1) {
      remainingPages.push({ handle: 'all', page });
    }
  }
  firstPages.forEach((result) => {
    for (let page = 2; page <= result.pages; page += 1) {
      remainingPages.push({ handle: result.handle, page });
    }
  });

  if (!remainingPages.length) {
    if (isCurrentRun()) {
      grid.dataset.categoryFilterLoaded = loadedKey;
      delete grid.dataset.categoryFilterLoading;
      syncCollectionVisibleCountFromDom();
    }
    return;
  }

  window.setTimeout(async () => {
    let nextPageIndex = 0;
    const workerCount = Math.min(BOOKSTORE_BACKGROUND_FILTER_WORKERS, Math.max(1, remainingPages.length));

    try {
      await Promise.all(
        Array.from({ length: workerCount }, async () => {
          while (nextPageIndex < remainingPages.length && isCurrentRun()) {
            const task = remainingPages[nextPageIndex];
            nextPageIndex += 1;
            await runBookstoreBackgroundTask(async () => {
              const result = await loadCategoryPage(task.handle, task.page);
              await appendCategoryItems(result.items);
            });
          }
        })
      );
    } finally {
      if (isCurrentRun()) {
        grid.dataset.categoryFilterLoaded = loadedKey;
        delete grid.dataset.categoryFilterLoading;
        filterExistingCollectionItemsByVendor(getCollectionVendorFilter());
        syncCollectionVisibleCountFromDom();
        setBookstoreFilterLoadingState(false);
      }
    }
  }, 50);
}

async function applyCollectionExtraCategoryFiltersFromURL(loadAllPages = false) {
  const selectedCategories = getBookstoreExtraCategoryFilters();
  if (!selectedCategories.length) return 0;

  if (loadAllPages) setBookstoreFilterLoadingState(true);

  let visibleCount = filterExistingCollectionItemsByVendor(getCollectionVendorFilter());

  try {
    if (loadAllPages) {
      await loadAllCollectionPagesForExtraCategoryFilters();
      visibleCount = filterExistingCollectionItemsByVendor(getCollectionVendorFilter());
    }
  } finally {
    if (loadAllPages) setBookstoreFilterLoadingState(false);
  }

  renderBookstoreActiveQueryPills();

  return visibleCount;
}

function initBookstoreCollectionVendorFilter() {
  const selectedVendors = getCollectionVendorFilters();

  if (!selectedVendors.length) {
    if (!hasBookstoreClientSideCollectionFilters()) {
      document.documentElement.classList.remove('bookstore-filtering-pending');
    }
    return;
  }

  applyCollectionVendorFilterFromURL(true);
  initBookstoreVendorFilterObserver();
}

function initBookstoreCollectionExtraCategoryFilter() {
  if (!getBookstoreExtraCategoryFilters().length) return;

  applyCollectionExtraCategoryFiltersFromURL(true);
  initBookstoreVendorFilterObserver();
}

function initBookstoreCollectionPriceFilter() {
  if (!hasBookstoreActivePriceFilter()) return;

  filterExistingCollectionItemsByVendor(getCollectionVendorFilter());
  initBookstoreVendorFilterObserver();
  renderBookstoreActiveQueryPills();

  if (!getCollectionVendorFilters().length) {
    loadAllCollectionPagesForExtraCategoryFilters();
  }
}

function initBookstoreVendorFilterObserver() {
  if (!hasBookstoreClientSideCollectionFilters()) return;

  const grid = getCollectionSearchGrid();
  if (!grid) return;
  if (window.__bookstoreVendorFilterObserverReady && window.__bookstoreVendorFilterObserverGrid === grid) return;

  window.__bookstoreVendorFilterObserverReady = true;
  window.__bookstoreVendorFilterObserverGrid = grid;
  let timer = 0;

  const observer = new MutationObserver(() => {
    if (grid.dataset.bookstoreBatchAppending === 'true' || grid.dataset.vendorFilterLoading || grid.dataset.categoryFilterLoading) return;

    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      filterExistingCollectionItemsByVendor(getCollectionVendorFilter());
    }, 50);
  });

  observer.observe(grid, { childList: true });
}

async function loadAllCollectionPagesForSearch(query) {
  const normalizedQuery = normalizeCollectionSearchText(query);
  if (!normalizedQuery) return;

  const resultsList = document.querySelector('main[data-template^="collection"] results-list[section-id]');
  const grid = getCollectionSearchGrid();

  if (!resultsList || !grid) return;

  const sectionId = resultsList.getAttribute('section-id');
  const lastPage = Number(grid.dataset.lastPage || 1);
  const loadedKey = `search:${normalizedQuery}`;

  if (!sectionId || !lastPage || lastPage <= 1 || grid.dataset.collectionSearchLoaded === loadedKey || grid.dataset.collectionSearchLoading === loadedKey) return;

  grid.dataset.collectionSearchLoading = loadedKey;
  const runId = (window.__bookstoreCollectionSearchRunId || 0) + 1;
  window.__bookstoreCollectionSearchRunId = runId;
  const isCurrentRun = () => window.__bookstoreCollectionSearchRunId === runId && grid.dataset.collectionSearchLoading === loadedKey;

  const existingIds = new Set(
    Array.from(grid.querySelectorAll('.product-grid__item[data-product-id]')).map((item) => item.getAttribute('data-product-id'))
  );

  try {
    for (let page = 2; page <= lastPage && isCurrentRun(); page += 1) {
      await runBookstoreBackgroundTask(async () => {
        if (!isCurrentRun()) return;

        const url = buildBookstoreSectionPageUrl(page, sectionId);
        const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok || !isCurrentRun()) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextItems = Array.from(doc.querySelectorAll('.product-grid__item[data-product-id]'));

        nextItems.forEach((item) => {
          const productId = item.getAttribute('data-product-id');
          if (!productId || existingIds.has(productId)) return;
          existingIds.add(productId);

          if (productItemMatchesSearch(item, query) && productItemMatchesSelectedVendors(item) && productItemMatchesActiveCategories(item) && productItemMatchesActivePrice(item)) {
            item.hidden = false;
            item.style.display = '';
            grid.appendChild(item);
          }
        });
      });
    }
  } catch (error) {
    // Keep already loaded matches if one extra page request fails.
  } finally {
    if (isCurrentRun()) {
      grid.dataset.collectionSearchLoaded = loadedKey;
    }
    if (grid.dataset.collectionSearchLoading === loadedKey) {
      delete grid.dataset.collectionSearchLoading;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionPublisherChips, { once: true });
} else {
  initCollectionPublisherChips();
}

document.addEventListener('shopify:section:load', initCollectionPublisherChips);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreCollectionVendorFilter, { once: true });
} else {
  initBookstoreCollectionVendorFilter();
}

document.addEventListener('shopify:section:load', initBookstoreCollectionVendorFilter);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreCollectionExtraCategoryFilter, { once: true });
} else {
  initBookstoreCollectionExtraCategoryFilter();
}

document.addEventListener('shopify:section:load', initBookstoreCollectionExtraCategoryFilter);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreCollectionPriceFilter, { once: true });
} else {
  initBookstoreCollectionPriceFilter();
}

document.addEventListener('shopify:section:load', initBookstoreCollectionPriceFilter);
window.addEventListener('popstate', initBookstoreCollectionPriceFilter);
document.addEventListener('bookstore:filters-updated', () => {
  window.setTimeout(() => {
    initBookstoreCollectionVendorFilter();
    initBookstoreCollectionExtraCategoryFilter();
    initBookstoreCollectionPriceFilter();
  }, 0);
});

/**
 * Collection/vendor hero search should filter the products on the same page.
 * Shopify's normal product search goes to /search, but the client requested
 * category and publisher pages to stay in place and behave like an in-page
 * catalogue search.
 */
function normalizeCollectionSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\u0900-\u097f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isLikelyIsbnSearch(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 8;
}

function getCollectionSearchItems() {
  return Array.from(document.querySelectorAll('main[data-template^="collection"] .product-grid__item'));
}

function getCollectionSearchGrid() {
  return document.querySelector('main[data-template^="collection"] .product-grid');
}

function buildBookstoreSectionPageUrl(page, sectionId, options = {}) {
  const url = new URL(window.location.href);

  if (options.omitClientSideFilters) {
    url.searchParams.delete('filter.p.vendor');
    url.searchParams.delete('bookstore_category');
  }

  url.searchParams.delete('page');
  url.searchParams.delete('section_id');
  url.searchParams.set('page', String(page));
  url.searchParams.set('section_id', sectionId);
  return url;
}

function rememberOriginalCollectionGrid() {
  const grid = getCollectionSearchGrid();
  const countNode = document.querySelector('main[data-template^="collection"] [data-bookstore-visible-count]');

  if (countNode && !countNode.dataset.bookstoreOriginalCount) {
    countNode.dataset.bookstoreOriginalCount = countNode.getAttribute('data-bookstore-total-count') || countNode.textContent.trim();
  }

  if (grid && !grid.dataset.bookstoreOriginalHtml) {
    grid.dataset.bookstoreOriginalHtml = grid.innerHTML;
    grid.dataset.bookstoreOriginalItemCount = String(getCollectionSearchItems().length);
  }
}

function restoreOriginalCollectionGrid() {
  const grid = getCollectionSearchGrid();
  const wrapper = document.querySelector('main[data-template^="collection"] collection-component');
  const message = document.querySelector('[data-bookstore-collection-search-empty]');

  if (grid?.dataset.bookstoreOriginalHtml) {
    grid.innerHTML = grid.dataset.bookstoreOriginalHtml;
    delete grid.dataset.collectionSearchMode;
    delete grid.dataset.collectionSearchLoaded;
  }

  if (wrapper) {
    wrapper.classList.remove('bookstore-collection-search-active');
  }

  if (message) {
    message.hidden = true;
  }

  setCollectionVisibleCount(getCollectionTotalCount());
}

function ensureCollectionSearchMessage(grid) {
  if (!grid) return null;

  let message = document.querySelector('[data-bookstore-collection-search-empty]');

  if (!message) {
    message = document.createElement('p');
    message.setAttribute('data-bookstore-collection-search-empty', '');
    message.className = 'bookstore-collection-search-empty';
    message.textContent = 'No books found for this search in this collection.';
    grid.insertAdjacentElement('afterend', message);
  }

  return message;
}

function updateCollectionSearchUrl(query) {
  const url = new URL(window.location.href);

  if (query) {
    url.searchParams.set('collection_search', query);
  } else {
    url.searchParams.delete('collection_search');
  }

  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  renderBookstoreActiveQueryPills();
}

function renderBookstoreActiveQueryPills() {
  const url = new URL(window.location.href);
  const query = (url.searchParams.get('collection_search') || '').trim();

  document.querySelectorAll('[data-bookstore-query-pill]').forEach((pill) => pill.remove());

  if (!query) return;

  document.querySelectorAll('.facets-remove').forEach((container) => {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill bookstore-active-query-pill';
    pill.setAttribute('data-bookstore-query-pill', '');
    pill.innerHTML = `<span>Search: ${escapeCollectionSearchHtml(query)}</span><span aria-hidden="true">×</span><span class="visually-hidden">Remove search filter</span>`;

    pill.addEventListener('click', () => {
      document.querySelectorAll('[data-bookstore-collection-search-input], input[name="collection_search"]').forEach((input) => {
        input.value = '';
      });
      applyCollectionPageSearch('', true, false);
      renderBookstoreActiveQueryPills();
    });

    container.prepend(pill);
  });
}

function getBookstoreFilterLabel(param) {
  const labels = {
    collection_search: 'Search',
    'filter.p.vendor': 'Publisher',
    'filter.p.m.custom.author': 'Author',
    'filter.p.m.custom.language': 'Language',
    'filter.p.m.custom.format': 'Format',
    'filter.v.availability': 'Availability',
    'filter.p.product_type': 'Category',
    'filter.p.m.custom.categories': 'Category',
    'filter.p.tag': 'Category',
  };

  if (labels[param]) return labels[param];

  return param
    .replace(/^filter\.[a-z]\./, '')
    .replace(/^m\.custom\./, '')
    .replace(/^custom\./, '')
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getBookstoreFilterValueLabel(param, value) {
  if (param === 'filter.v.availability') {
    if (value === '1') return 'In stock';
    if (value === '0') return 'Out of stock';
  }

  return value;
}

function getBookstoreCollectionPathFilter(url) {
  const pathParts = url.pathname.split('/').filter(Boolean);
  if (pathParts[0] !== 'collections') return null;

  const handle = pathParts[1] || '';
  if (!handle || handle === 'all' || handle === 'vendors' || handle === 'frontpage') return null;

  const decodedHandle = decodeURIComponent(handle.replace(/\+/g, ' '));
  const heading = document.querySelector('main h1');
  const headingText = (heading?.textContent || '').trim();
  const fallbackLabel = decodedHandle
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return {
    param: 'bookstore_collection_page',
    label: 'Category',
    value: handle,
    displayValue: headingText || fallbackLabel,
  };
}

function getBookstoreCategoryFilterLabel(handle) {
  const normalizedHandle = String(handle || '').trim();
  if (!normalizedHandle) return '';

  const link = Array.from(document.querySelectorAll('.bookstore-collection-facet__link[data-bookstore-category-handle]')).find((candidate) => {
    return candidate.getAttribute('data-bookstore-category-handle') === normalizedHandle;
  });

  if (link) {
    return link.getAttribute('data-bookstore-category-title') || link.textContent.trim();
  }

  return normalizedHandle
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function removeSingleBookstoreSearchParam(params, name, value) {
  const values = params.getAll(name);
  let removed = false;

  params.delete(name);

  values.forEach((currentValue) => {
    if (!removed && currentValue === value) {
      removed = true;
      return;
    }

    params.append(name, currentValue);
  });
}

function renderBookstoreActiveQueryPills() {
  const url = new URL(window.location.href);
  const activeFilters = [];
  const handledParams = new Set(['filter.v.price.gte', 'filter.v.price.lte']);
  const query = (url.searchParams.get('collection_search') || '').trim();
  const minPrice = (url.searchParams.get('filter.v.price.gte') || '').trim();
  const maxPrice = (url.searchParams.get('filter.v.price.lte') || '').trim();
  const collectionPathFilter = getBookstoreCollectionPathFilter(url);

  if (collectionPathFilter) {
    activeFilters.push(collectionPathFilter);
  }

  getBookstoreExtraCategoryFilters(url).forEach((handle) => {
    activeFilters.push({
      param: 'bookstore_category',
      label: 'Category',
      value: handle,
      displayValue: getBookstoreCategoryFilterLabel(handle),
    });
  });

  if (query && query !== '*') {
    activeFilters.push({
      param: 'collection_search',
      label: 'Search',
      value: query,
    });
  }

  if (minPrice || maxPrice) {
    activeFilters.push({
      param: 'bookstore_price_range',
      label: 'Price',
      value: `${minPrice || '0'} - ${maxPrice || 'Max'}`,
    });
  }

  if (url.pathname === '/collections/vendors' && !url.searchParams.has('filter.p.vendor')) {
    const currentVendor = (url.searchParams.get('q') || '').trim();

    if (currentVendor && currentVendor !== '*') {
      activeFilters.push({
        param: 'bookstore_vendor_page',
        label: 'Publisher',
        value: currentVendor,
      });
    }
  }

  for (const [param, rawValue] of url.searchParams.entries()) {
    const value = (rawValue || '').trim();
    if (!param.startsWith('filter.') || handledParams.has(param) || !value || value === '*') continue;

    activeFilters.push({
      param,
      label: getBookstoreFilterLabel(param),
      value,
      displayValue: getBookstoreFilterValueLabel(param, value),
    });
  }

  if (!url.searchParams.has('filter.p.m.custom.author')) {
    const author = (url.searchParams.get('author') || '').trim();
    if (author && author !== '*') {
      activeFilters.push({
        param: 'author',
        label: 'Author',
        value: author,
      });
    }
  }

  document.querySelectorAll('[data-bookstore-query-pill]').forEach((pill) => pill.remove());
  renderBookstoreSelectedFilterSummary();

  if (!activeFilters.length) return;

  document.querySelectorAll('.facets-remove').forEach((container) => {
    activeFilters.slice().reverse().forEach((filter) => {
      const normalizedContainerText = normalizeCollectionSearchText(container.textContent || '');
      const normalizedValue = normalizeCollectionSearchText(filter.value);
      if (normalizedValue && normalizedContainerText.includes(normalizedValue)) return;

      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill bookstore-active-query-pill';
      pill.setAttribute('data-bookstore-query-pill', '');
      pill.setAttribute('data-bookstore-query-param', filter.param);
      pill.innerHTML = `<span>${escapeCollectionSearchHtml(filter.label)}: ${escapeCollectionSearchHtml(filter.value)}</span><span aria-hidden="true">×</span><span class="visually-hidden">Remove ${escapeCollectionSearchHtml(filter.label)} filter</span>`;

      pill.innerHTML = `<span>${escapeCollectionSearchHtml(filter.label)}: ${escapeCollectionSearchHtml(filter.displayValue || filter.value)}</span><span aria-hidden="true">&times;</span><span class="visually-hidden">Remove ${escapeCollectionSearchHtml(filter.label)} filter</span>`;

      pill.addEventListener('click', () => {
        const nextUrl = new URL(window.location.href);

        if (filter.param === 'collection_search') {
          nextUrl.searchParams.delete(filter.param);
          document.querySelectorAll('[data-bookstore-collection-search-input], input[name="collection_search"]').forEach((input) => {
            input.value = '';
          });
          applyCollectionPageSearch('', true, false);
          renderBookstoreActiveQueryPills();
          return;
        }

        if (filter.param === 'bookstore_price_range') {
          nextUrl.searchParams.delete('filter.v.price.gte');
          nextUrl.searchParams.delete('filter.v.price.lte');
        } else if (filter.param === 'bookstore_vendor_page') {
          nextUrl.pathname = '/collections/all';
          nextUrl.searchParams.delete('q');
          nextUrl.searchParams.delete('type');
          nextUrl.searchParams.delete('view');
          nextUrl.searchParams.delete('options[prefix]');
        } else if (filter.param === 'bookstore_collection_page') {
          nextUrl.pathname = '/collections/all';
        } else {
          removeSingleBookstoreSearchParam(nextUrl.searchParams, filter.param, filter.value);
        }

        if (filter.param === 'filter.p.m.custom.author' || filter.param === 'author') {
          nextUrl.searchParams.delete('author');
          if (nextUrl.searchParams.get('view') === 'author') nextUrl.searchParams.delete('view');
        }

        nextUrl.searchParams.delete('page');
        window.location.href = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      });

      container.prepend(pill);
    });
  });
}

function isExactCollectionPublisherQuery(query) {
  const normalizedQuery = normalizeCollectionSearchText(query);
  if (!normalizedQuery) return false;

  return Array.from(document.querySelectorAll('.bookstore-category-publishers a')).some((chip) => {
    return normalizeCollectionSearchText(getCollectionPublisherChipValue(chip)) === normalizedQuery;
  });
}

function shouldUseGlobalCollectionSearch(query) {
  const normalizedQuery = normalizeCollectionSearchText(query);
  if (!normalizedQuery) return false;
  if (isExactCollectionPublisherQuery(query)) return false;
  if (isLikelyIsbnSearch(query)) return false;

  return window.location.pathname.replace(/\/$/, '') === '/collections/all';
}

function escapeCollectionSearchHtml(value) {
  return String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  })[char]);
}

function formatPredictiveSearchPrice(product) {
  const price = product?.price_min || product?.price || '';
  const numericPrice = Number(String(price).replace(/,/g, ''));

  if (Number.isFinite(numericPrice)) {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'INR',
        maximumFractionDigits: numericPrice % 1 === 0 ? 0 : 2,
      }).format(numericPrice);
    } catch (error) {
      return `Rs. ${numericPrice.toFixed(numericPrice % 1 === 0 ? 0 : 2)}`;
    }
  }

  return price ? `Rs. ${price}` : '';
}

function renderPredictiveCollectionSearchItem(product) {
  const productId = product?.id || product?.handle || product?.url || '';
  const title = product?.title || '';
  const vendor = product?.vendor || '';
  const productType = product?.type || '';
  const url = product?.url || (product?.handle ? `/products/${product.handle}` : '#');
  const image = product?.featured_image?.url || product?.image || '';
  const price = formatPredictiveSearchPrice(product);
  const searchText = [title, vendor, productType, product?.tags?.join?.(' ')].filter(Boolean).join(' ');

  return `
    <li
      class="product-grid__item bookstore-predictive-collection-item"
      data-product-id="${escapeCollectionSearchHtml(productId)}"
      data-collection-search-text="${escapeCollectionSearchHtml(searchText)}"
    >
      <product-card class="product-card size-style bookstore-predictive-collection-card" data-product-id="${escapeCollectionSearchHtml(productId)}">
        <a class="bookstore-predictive-collection-card__image" href="${escapeCollectionSearchHtml(url)}" aria-label="${escapeCollectionSearchHtml(title)}">
          ${
            image
              ? `<img src="${escapeCollectionSearchHtml(image)}" alt="${escapeCollectionSearchHtml(title)}" loading="lazy">`
              : `<span>${escapeCollectionSearchHtml(title.slice(0, 1) || 'B')}</span>`
          }
        </a>
        <div class="bookstore-predictive-collection-card__body">
          ${vendor ? `<p>${escapeCollectionSearchHtml(vendor)}</p>` : ''}
          <h3><a href="${escapeCollectionSearchHtml(url)}">${escapeCollectionSearchHtml(title)}</a></h3>
          ${price ? `<div class="bookstore-predictive-collection-card__price">${escapeCollectionSearchHtml(price)}</div>` : ''}
        </div>
      </product-card>
    </li>
  `;
}

async function loadPredictiveCollectionProducts(query) {
  const url = new URL('/search/suggest.json', window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '10');
  url.searchParams.set('resources[limit_scope]', 'each');
  url.searchParams.set('resources[options][unavailable_products]', 'last');
  url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');

  const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;

  const payload = await response.json();
  return payload?.resources?.results?.products || [];
}

async function loadGlobalCollectionSearch(query) {
  const normalizedQuery = normalizeCollectionSearchText(query);
  if (!normalizedQuery) return false;

  const grid = getCollectionSearchGrid();
  const wrapper = document.querySelector('main[data-template^="collection"] collection-component');
  const message = ensureCollectionSearchMessage(grid);

  if (!grid) return false;

  rememberOriginalCollectionGrid();

  if (message) {
    message.textContent = 'Finding matching books...';
    message.hidden = false;
  }

  try {
    const predictiveProducts = await loadPredictiveCollectionProducts(query);

    if (Array.isArray(predictiveProducts)) {
      grid.innerHTML = predictiveProducts.map(renderPredictiveCollectionSearchItem).join('');
      grid.dataset.collectionSearchMode = 'predictive';

      if (wrapper) {
        wrapper.classList.toggle('bookstore-collection-search-active', true);
      }

      setCollectionVisibleCount(predictiveProducts.length);

      if (message) {
        message.textContent = 'No books found for this search in this collection.';
        message.hidden = predictiveProducts.length > 0;
      }

      return true;
    }
  } catch (error) {
    // Fall back to full search page parsing below if the predictive endpoint is unavailable.
  }

  const url = new URL('/search', window.location.origin);
  url.searchParams.set('type', 'product');
  url.searchParams.set('q', query);
  url.searchParams.set('options[prefix]', 'last');

  try {
    const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) return false;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const searchItems = Array.from(doc.querySelectorAll('main[data-template^="search"] .product-grid__item[data-product-id], .product-grid__item[data-product-id]'))
      .filter((item) => productItemMatchesSearch(item, query) && productItemMatchesSelectedVendors(item) && productItemMatchesActiveCategories(item) && productItemMatchesActivePrice(item));
    const resultCount = searchItems.length;

    grid.innerHTML = '';
    searchItems.forEach((item) => {
      item.hidden = false;
      item.style.display = '';
      grid.appendChild(item);
    });

    grid.dataset.collectionSearchMode = 'global';

    if (wrapper) {
      wrapper.classList.toggle('bookstore-collection-search-active', true);
    }

    setCollectionVisibleCount(resultCount);

    if (message) {
      message.textContent = 'No books found for this search in this collection.';
      message.hidden = resultCount > 0;
    }

    return true;
  } catch (error) {
    if (message) {
      message.textContent = 'No books found for this search in this collection.';
      message.hidden = true;
    }
  }

  return false;
}

async function applyCollectionPageSearch(query, updateUrl = true, loadAllPages = false) {
  rememberOriginalCollectionGrid();
  syncCollectionPublisherChips(query);

  const normalizedQuery = normalizeCollectionSearchText(query);
  const tokens = normalizedQuery.split(' ').filter(Boolean);
  const items = getCollectionSearchItems();
  const grid = getCollectionSearchGrid();
  const wrapper = document.querySelector('main[data-template^="collection"] collection-component');
  let visibleCount = 0;

  if (!items.length) return;

  if (tokens.length === 0) {
    restoreOriginalCollectionGrid();

    if (updateUrl) {
      updateCollectionSearchUrl('');
    }

    return;
  }

  if (loadAllPages && shouldUseGlobalCollectionSearch(query)) {
    if (updateUrl) {
      updateCollectionSearchUrl(query);
    }

    const usedGlobalSearch = await loadGlobalCollectionSearch(query);
    if (usedGlobalSearch) return;
  }

  items.forEach((item) => {
    const isMatch = productItemMatchesSearch(item, query) && productItemMatchesSelectedVendors(item) && productItemMatchesActiveCategories(item) && productItemMatchesActivePrice(item);

    item.hidden = !isMatch;
    item.style.display = isMatch ? '' : 'none';

    if (isMatch) visibleCount += 1;
  });

  const message = ensureCollectionSearchMessage(grid);

  if (message) {
    message.hidden = tokens.length === 0 || visibleCount > 0;
    if (tokens.length > 0 && visibleCount === 0 && loadAllPages) {
      message.textContent = 'Finding matching books...';
      message.hidden = false;
    } else {
      message.textContent = 'No books found for this search in this collection.';
    }
  }

  if (wrapper) {
    wrapper.classList.toggle('bookstore-collection-search-active', tokens.length > 0);
  }

  setCollectionVisibleCount(visibleCount);

  if (updateUrl) {
    updateCollectionSearchUrl(query);
  }

  if (tokens.length > 0 && loadAllPages) {
    await loadAllCollectionPagesForSearch(query);

    if (getCollectionSearchItems().length !== items.length) {
      return applyCollectionPageSearch(query, false, false);
    }

    if (message) {
      message.textContent = 'No books found for this search in this collection.';
      message.hidden = visibleCount > 0;
    }
  }
}

function initCollectionPageSearch() {
  const forms = Array.from(document.querySelectorAll('[data-bookstore-collection-search]'));

  forms.forEach((form) => {
    const input = form.querySelector('[data-bookstore-collection-search-input], input[name="collection_search"]');

    if (!input || form.dataset.collectionSearchReady === 'true') return;

    form.dataset.collectionSearchReady = 'true';

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      applyCollectionPageSearch(input.value || '', true, true);
    });

    input.addEventListener('input', () => {
      window.clearTimeout(input._bookstoreCollectionSearchTimer);
      input._bookstoreCollectionSearchTimer = window.setTimeout(() => {
        applyCollectionPageSearch(input.value || '', true, false);
      }, BOOKSTORE_COLLECTION_SEARCH_DEBOUNCE);
    });

    const initialQuery = new URL(window.location.href).searchParams.get('collection_search') || input.value || '';

    if (initialQuery) {
      input.value = initialQuery;
      applyCollectionPageSearch(initialQuery, false, true);
    }
  });

  renderBookstoreActiveQueryPills();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionPageSearch, { once: true });
} else {
  initCollectionPageSearch();
}

document.addEventListener('shopify:section:load', initCollectionPageSearch);

function upsertHiddenInput(form, name, value) {
  if (!form || !name || value == null) return;

  let input = Array.from(form.querySelectorAll(`input[type="hidden"][name="${CSS.escape(name)}"]`)).find((candidate) => {
    return candidate.dataset.bookstoreInjected === 'true' || candidate.value === value;
  });

  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.dataset.bookstoreInjected = 'true';
    form.prepend(input);
  }

  input.value = value;
}

function initBookstoreAuthorFilterLock() {
  const url = new URL(window.location.href);

  if (url.pathname !== '/search') return;

  const view = url.searchParams.get('view') || '';
  const author = url.searchParams.get('author') || url.searchParams.get('filter.p.m.custom.author') || '';

  if (view !== 'author' || !author || author === '*') return;

  document.querySelectorAll('form[id^="FacetFiltersForm--"]').forEach((form) => {
    upsertHiddenInput(form, 'view', 'author');
    upsertHiddenInput(form, 'type', 'product');
    upsertHiddenInput(form, 'q', '*');
    upsertHiddenInput(form, 'author', author);
    upsertHiddenInput(form, 'filter.p.m.custom.author', author);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreAuthorFilterLock, { once: true });
} else {
  initBookstoreAuthorFilterLock();
}

document.addEventListener('shopify:section:load', initBookstoreAuthorFilterLock);

function initBookstoreSortPills() {
  document.querySelectorAll('[data-bookstore-sort-pill]').forEach((pill) => {
    if (pill.dataset.bookstoreSortReady === 'true') return;

    pill.dataset.bookstoreSortReady = 'true';
    pill.addEventListener('click', (event) => {
      const sortValue = pill.getAttribute('data-sort-value');

      if (!sortValue) return;

      event.preventDefault();

      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortValue);
      url.searchParams.delete('page');
      window.location.href = `${url.pathname}${url.search}${url.hash}`;
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreSortPills, { once: true });
} else {
  initBookstoreSortPills();
}

document.addEventListener('shopify:section:load', initBookstoreSortPills);

const titleCaseBookstoreAuthorName = (authorName) => String(authorName || '')
  .trim()
  .toLowerCase()
  .replace(/\b([a-z])/g, (match) => match.toUpperCase());

function initBookstoreAuthorUrlCanonicalizer() {
  const url = new URL(window.location.href);
  if (url.pathname !== '/search' || url.searchParams.get('view') !== 'author') return;

  const authorName = (url.searchParams.get('author') || url.searchParams.get('filter.p.m.custom.author') || '').trim();
  if (!authorName || authorName === '*' || /[A-Z]/.test(authorName)) return;

  const canonicalAuthorName = titleCaseBookstoreAuthorName(authorName);
  if (!canonicalAuthorName || canonicalAuthorName === authorName) return;

  url.searchParams.set('author', canonicalAuthorName);
  url.searchParams.set('filter.p.m.custom.author', canonicalAuthorName);
  url.searchParams.delete('page');
  window.location.replace(`${url.pathname}${url.search}${url.hash}`);
}

function initBookstoreAuthorSidebarSearch() {
  document.querySelectorAll('[data-author-sidebar-search]').forEach((container) => {
    if (container.dataset.bookstoreAuthorSidebarReady === 'true') return;

    container.dataset.bookstoreAuthorSidebarReady = 'true';
    const input = container.querySelector('[data-author-search-input], input[name="author"]');
    const button = container.querySelector('[data-author-search-submit], button[type="submit"], button');
    const filterInput = container.querySelector('[data-author-sidebar-filter-value]');
    const details = container.closest('.bookstore-entity-facet--author-search');
    const rows = Array.from(details?.querySelectorAll('.bookstore-author-filter-row') || []);

    const getAuthorName = () => String(input?.value || '').trim();

    const findVisibleCanonicalAuthor = (authorName) => {
      const normalizedAuthor = normalizeCollectionSearchText(authorName);
      if (!normalizedAuthor) return '';

      const exactRow = rows.find((row) => {
        return normalizeCollectionSearchText(row.textContent || '') === normalizedAuthor;
      });

      if (exactRow) return String(exactRow.textContent || '').trim();

      const startsWithRow = rows.find((row) => {
        return normalizeCollectionSearchText(row.textContent || '').startsWith(normalizedAuthor);
      });

      return startsWithRow ? String(startsWithRow.textContent || '').trim() : '';
    };

    const syncAuthorValue = () => {
      const authorName = getAuthorName();
      if (filterInput) filterInput.value = authorName;
      return authorName;
    };

    const filterAuthorRows = () => {
      const query = normalizeCollectionSearchText(getAuthorName());
      rows.forEach((row) => {
        const item = row.closest('.facets__inputs-list-item');
        const label = normalizeCollectionSearchText(row.textContent || '');
        const isMatch = !query || label.includes(query);
        if (item) item.hidden = !isMatch;
      });
    };

    const openAuthorSearch = () => {
      const typedAuthorName = syncAuthorValue();
      const authorName = findVisibleCanonicalAuthor(typedAuthorName) || titleCaseBookstoreAuthorName(typedAuthorName);
      if (!authorName) return;

      const url = new URL('/search', window.location.origin);
      url.searchParams.set('view', 'author');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', '*');
      url.searchParams.set('filter.p.m.custom.author', authorName);
      url.searchParams.set('author', authorName);
      window.location.href = `${url.pathname}${url.search}`;
    };

    input?.addEventListener('input', () => {
      syncAuthorValue();
      filterAuthorRows();
    });

    const handleAuthorSearchEnter = (event) => {
      if (event.key !== 'Enter' && event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
      event.preventDefault();
      event.stopPropagation();
      openAuthorSearch();
    };

    input?.addEventListener('keydown', handleAuthorSearchEnter, true);
    input?.addEventListener('keypress', handleAuthorSearchEnter, true);
    input?.addEventListener('keyup', handleAuthorSearchEnter, true);

    button?.addEventListener('click', (event) => {
      event.preventDefault();
      openAuthorSearch();
    });

    container.addEventListener('submit', (event) => {
      event.preventDefault();
      openAuthorSearch();
    });

  });

  if (!window.__bookstoreAuthorSearchEnterFallbackReady) {
    window.__bookstoreAuthorSearchEnterFallbackReady = true;
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
        const target = event.target instanceof Element ? event.target : null;
        const input = target?.closest?.('[data-author-search-input]');
        if (!input) return;

        const container = input.closest('[data-author-sidebar-search]');
        const button = container?.querySelector('[data-author-search-submit]');
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();
        button.click();
      },
      true
    );
  }

  document.querySelectorAll('[data-author-filter-option]').forEach((input) => {
    if (input.dataset.bookstoreAuthorOptionReady === 'true') return;

    input.dataset.bookstoreAuthorOptionReady = 'true';
    input.addEventListener('change', () => {
      const targetUrl = input.getAttribute('data-author-url');
      if (targetUrl) window.location.href = targetUrl;
    });
  });
}

function initBookstorePolicyHeadings() {
  const path = window.location.pathname.replace(/\/$/, '');
  const title =
    document.querySelector('.shopify-policy__title h1') ||
    document.querySelector('.shopify-policy__title') ||
    document.querySelector('main h1');

  if (!title) return;

  if (path === '/policies/terms-of-service') {
    title.textContent = 'Terms & Conditions';
  }

  if (path === '/policies/legal-notice') {
    title.textContent = 'Copyright Policy';
  }
}

function initBookstoreDocChangeFixes() {
  initBookstoreAuthorUrlCanonicalizer();
  initBookstoreAuthorSidebarSearch();
  initBookstorePolicyHeadings();
  renderBookstoreActiveQueryPills();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreDocChangeFixes, { once: true });
} else {
  initBookstoreDocChangeFixes();
}

document.addEventListener('shopify:section:load', initBookstoreDocChangeFixes);

/**
 * A few imported book records contain mojibake fragments in visible card text
 * (for example: "Madan Lal √¢ ,Ç¨ÀÜ..."). Until the source data is cleaned,
 * remove the visibly broken suffix from cards so customers do not see garbage
 * characters in collection/homepage grids.
 */
function cleanBookstoreMojibakeText(value) {
  const text = String(value || '');
  if (!/[√�]|‚Ç|,Ç|ÀÜ/.test(text)) return text;

  return text
    .split('√')[0]
    .replace(/�+/g, '')
    .replace(/\s*,?Ç[^\s]*/g, '')
    .replace(/\s*‚Ç[^\s]*/g, '')
    .replace(/\s*ÀÜ[^\s]*/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .trim();
}

function sanitizeBookstoreVisibleText(root = document) {
  const scope = root instanceof Element ? root : document;
  const nodes = scope.querySelectorAll?.(
    [
      '.product-grid__item h3',
      '.product-grid__item h4',
      '.product-grid__item p',
      '.product-grid__item span',
      '.product-grid__item strong',
      '.sb-publisher-book strong',
      '.sb-publisher-book small',
      '.rk-product-card__author',
      '.rk-product-card__title',
      '.rk-product-card__publisher',
      '.sb-book-card h3',
      '.sb-book-card p',
    ].join(',')
  );

  if (!nodes) return;

  nodes.forEach((node) => {
    if (node.children.length > 0) return;
    const cleaned = cleanBookstoreMojibakeText(node.textContent);
    if (cleaned && cleaned !== node.textContent) {
      node.textContent = cleaned;
    }
  });
}

function initBookstoreTextSanitizer() {
  sanitizeBookstoreVisibleText(document);

  if (window.__bookstoreTextSanitizerReady) return;
  window.__bookstoreTextSanitizerReady = true;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) sanitizeBookstoreVisibleText(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreTextSanitizer, { once: true });
} else {
  initBookstoreTextSanitizer();
}

document.addEventListener('shopify:section:load', () => sanitizeBookstoreVisibleText(document));

function getBookstorePreservedCategoryParams() {
  const current = new URL(window.location.href);
  const params = new URLSearchParams();

  for (const [name, value] of current.searchParams.entries()) {
    if (!value || name === 'page' || name === 'section_id') continue;

    if (
      name.startsWith('filter.') ||
      name === 'author' ||
      name === 'collection_search' ||
      name === 'bookstore_category' ||
      name === 'sort_by'
    ) {
      params.append(name, value);
    }
  }

  if (current.pathname === '/collections/vendors') {
    const vendor = current.searchParams.get('q') || '';
    if (vendor && !params.has('filter.p.vendor')) {
      params.set('filter.p.vendor', vendor);
    }
  }

  return params;
}

function appendUniqueBookstoreSearchParam(params, name, value) {
  if (!name || !value) return;

  const normalizedValue = normalizeCollectionSearchText(value);
  const alreadyExists = params.getAll(name).some((currentValue) => {
    return normalizeCollectionSearchText(currentValue) === normalizedValue;
  });

  if (!alreadyExists) {
    params.append(name, value);
  }
}

function rewriteBookstoreCategoryFilterLinks(root = document) {
  const scope = root instanceof Element ? root : document;
  const current = new URL(window.location.href);
  const currentHandle = getBookstoreCollectionHandleFromPath(current.pathname);
  const selectedCategories = getBookstoreExtraCategoryFilters(current);
  const preserved = getBookstorePreservedCategoryParams();

  scope.querySelectorAll?.('.bookstore-collection-facet__link[href*="/collections/"]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const target = new URL(link.href, window.location.origin);
    if (!target.pathname.startsWith('/collections/')) return;
    if (target.pathname === '/collections/vendors') return;

    const targetHandle = link.getAttribute('data-bookstore-category-handle') || getBookstoreCollectionHandleFromPath(target.pathname);

    if (!targetHandle) return;

    if (targetHandle === 'all') {
      target.search = '';

      preserved.forEach((value, name) => {
        if (name === 'bookstore_category') return;
        appendUniqueBookstoreSearchParam(target.searchParams, name, value);
      });

      target.searchParams.delete('page');
      target.searchParams.delete('section_id');
      link.href = `${target.pathname}${target.search}${target.hash}`;

      const isAllActive = !currentHandle && selectedCategories.length === 0;
      link.classList.toggle('is-active', isAllActive);
      if (isAllActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }

      return;
    }

    const isSelectedExtraCategory = selectedCategories.includes(targetHandle);

    if (currentHandle && currentHandle !== targetHandle) {
      const next = new URL(current.href);

      next.searchParams.delete('page');
      next.searchParams.delete('section_id');

      if (isSelectedExtraCategory) {
        removeSingleBookstoreSearchParam(next.searchParams, 'bookstore_category', targetHandle);
      } else {
        next.searchParams.append('bookstore_category', targetHandle);
      }

      link.href = `${next.pathname}${next.search}${next.hash}`;
    } else {
      preserved.forEach((value, name) => {
        appendUniqueBookstoreSearchParam(target.searchParams, name, value);
      });

      target.searchParams.delete('page');
      target.searchParams.delete('section_id');
      link.href = `${target.pathname}${target.search}${target.hash}`;
    }

    const isActive = currentHandle === targetHandle || isSelectedExtraCategory;
    link.classList.toggle('is-active', isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });
}

function rewriteBookstoreSortPillLinks(root = document) {
  const scope = root instanceof Element ? root : document;
  const current = new URL(window.location.href);

  scope.querySelectorAll?.('[data-bookstore-sort-pill]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const target = new URL(current.href);
    const pillUrl = new URL(link.href, window.location.origin);
    const sortValue = link.dataset.sortValue || pillUrl.searchParams.get('sort_by') || '';

    if (!sortValue) return;

    target.searchParams.set('sort_by', sortValue);
    target.searchParams.delete('page');
    link.href = `${target.pathname}${target.search}${target.hash}`;
  });
}

function initBookstoreCategoryFilterLinks() {
  rewriteBookstoreCategoryFilterLinks(document);
  rewriteBookstoreSortPillLinks(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookstoreCategoryFilterLinks, { once: true });
} else {
  initBookstoreCategoryFilterLinks();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof Element ? event.target : document;
  rewriteBookstoreCategoryFilterLinks(target);
  rewriteBookstoreSortPillLinks(target);
});

function initBookstoreEntityFilterTools(root = document) {
  const scope = root instanceof Element ? root : document;

  scope.querySelectorAll?.('[data-entity-filter-search]').forEach((container) => {
    if (container.dataset.bookstoreEntitySearchReady === 'true') return;

    container.dataset.bookstoreEntitySearchReady = 'true';
    const input = container.querySelector('[data-entity-filter-search-input]');
    const button = container.querySelector('[data-entity-filter-search-button]');
    const details = container.closest('.bookstore-entity-facet');
    const rows = Array.from(details?.querySelectorAll('.facets__inputs-list-item') || []);

    const applySearch = () => {
      const query = normalizeCollectionSearchText(input?.value || '');
      rows.forEach((row) => {
        const label = normalizeCollectionSearchText(row.textContent || '');
        const matches = !query || label.includes(query);
        row.hidden = !matches;
        if (matches) row.removeAttribute('data-entity-extra-item');
      });
    };

    input?.addEventListener('input', applySearch);
    input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
      event.preventDefault();
      event.stopPropagation();
      applySearch();
    });
    button?.addEventListener('click', (event) => {
      event.preventDefault();
      applySearch();
    });
  });

  scope.querySelectorAll?.('[data-entity-show-more]').forEach((button) => {
    if (button.dataset.bookstoreEntityShowMoreReady === 'true') return;

    button.dataset.bookstoreEntityShowMoreReady = 'true';
    button.addEventListener('click', () => {
      const details = button.closest('.bookstore-entity-facet');
      details?.querySelectorAll('[data-entity-extra-item]').forEach((item) => {
        item.hidden = false;
        item.removeAttribute('data-entity-extra-item');
      });
      button.remove();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBookstoreEntityFilterTools(document), { once: true });
} else {
  initBookstoreEntityFilterTools(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initBookstoreEntityFilterTools(event.target instanceof Element ? event.target : document);
});
